import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentIcon,
  CodeBracketSquareIcon,
  EyeIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import fileFormatService from "../../services/fileFormatService";

export const FORMAT_META = {
  docx: { label: "Word", ext: "DOCX", accept: ".docx,.dotx", hint: "Word template (.docx/.dotx). Add a paragraph with {{CONTENT}} where generated content should go." },
  pdf: { label: "PDF", ext: "PDF", accept: null, hint: "PDF has no template file — configure fonts, colors, page, header, footer and logo in the style JSON." },
  xlsx: { label: "Excel", ext: "XLSX", accept: ".xlsx,.xltx", hint: "Excel template (.xlsx/.xltx). Each sheet's sample table is replaced by the matching generated table (by sheet name / columns); banner rows and logos are kept." },
  pptx: { label: "PowerPoint", ext: "PPTX", accept: ".pptx,.potx", hint: "PowerPoint template (.pptx/.potx). Slides are built from the template's own slides (logo, header/footer, title bar) or, if it has none, its layouts and theme." },
  csv: { label: "CSV", ext: "CSV", accept: ".csv", hint: "CSV whose header row defines the column order." },
  md: { label: "Markdown", ext: "MD", accept: ".md,.markdown", hint: "UTF-8 text containing {{CONTENT}}; {{TITLE}}, {{DATE}}, {{FACILITY_NAME}} are also filled." },
  txt: { label: "Text", ext: "TXT", accept: ".txt", hint: "UTF-8 text containing {{CONTENT}}; {{TITLE}}, {{DATE}}, {{FACILITY_NAME}} are also filled." },
};

// Pull a readable message out of an axios error. Blob responses (template
// download / preview) carry their JSON error body as a Blob.
export const apiErrorMessage = async (error, fallback = "Request failed") => {
  let data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      data = JSON.parse(await data.text());
    } catch {
      data = null;
    }
  }
  const detail = data?.detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => `${(d.loc || []).filter((p) => p !== "body").join(".") || "value"}: ${d.msg}`)
      .join("; ");
  }
  if (typeof detail === "string") return detail;
  return error?.message || fallback;
};

export const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value.endsWith?.("Z") || value.includes?.("+") ? value : `${value}Z`);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
};

export const ToggleSwitch = ({ isActive, onToggle, disabled = false, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={isActive}
    aria-label={label}
    disabled={disabled}
    onClick={onToggle}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
      isActive ? "bg-green-500" : "bg-gray-300"
    } ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
  >
    <span
      aria-hidden="true"
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out ${
        isActive ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);

const toOutlineRows = (outline) =>
  (outline || []).map((item, index) => ({
    key: `${index}-${item.heading}`,
    heading: item.heading || "",
    level: item.level ?? 1,
    guidance: item.guidance || "",
  }));

// Keys edited by the dedicated "Dynamic values" section rather than the raw JSON.
const DYNAMIC_KEYS = ["fields", "text_replacements", "date_format"];
const BUILTIN_TOKENS = new Set(["CONTENT", "TITLE", "SUBTITLE", "DATE", "FACILITY_NAME", "PAGE"]);
const TOKEN_PATTERN = /\{\{([A-Z][A-Z0-9_]{0,39})\}\}/g;

const splitStyle = (styleConfig) => {
  const rest = { ...(styleConfig || {}) };
  const fields = (rest.fields || []).map((f, i) => ({
    key: `f-${i}-${f.name}`,
    name: f.name || "",
    description: f.description || "",
    default: f.default || "",
  }));
  const replacements = Object.entries(rest.text_replacements || {}).map(([find, replace], i) => ({
    key: `r-${i}`,
    find,
    replace,
  }));
  const dateFormat = rest.date_format || "";
  DYNAMIC_KEYS.forEach((k) => delete rest[k]);
  return { rest, fields, replacements, dateFormat };
};

// One named facility template (GPI-1256). A format can have several; at most
// one is the default. `row` is the template as returned by the admin API.
const FileFormatCard = ({ facilityNumber, row, schema, onChanged }) => {
  const fileFormat = row.file_format;
  const templateId = row.id;
  const meta = FORMAT_META[fileFormat];
  const fileInputRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(null); // which action is running
  const [name, setName] = useState(row.name || "");
  const [description, setDescription] = useState(row.description || "");
  const [instructions, setInstructions] = useState(row?.content_instructions || "");
  const [outline, setOutline] = useState(toOutlineRows(row?.content_outline));
  const initial = splitStyle(row?.style_config);
  const [styleText, setStyleText] = useState(
    Object.keys(initial.rest).length ? JSON.stringify(initial.rest, null, 2) : ""
  );
  const [fields, setFields] = useState(initial.fields);
  const [replacements, setReplacements] = useState(initial.replacements);
  const [dateFormat, setDateFormat] = useState(initial.dateFormat);
  const [styleError, setStyleError] = useState(null);
  const [showDefaults, setShowDefaults] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importError, setImportError] = useState(null);
  const importFileRef = useRef(null);

  // Reset the editor whenever the saved row changes (after a save/upload).
  useEffect(() => {
    const next = splitStyle(row?.style_config);
    setName(row.name || "");
    setDescription(row.description || "");
    setInstructions(row?.content_instructions || "");
    setOutline(toOutlineRows(row?.content_outline));
    setStyleText(Object.keys(next.rest).length ? JSON.stringify(next.rest, null, 2) : "");
    setFields(next.fields);
    setReplacements(next.replacements);
    setDateFormat(next.dateFormat);
    setStyleError(null);
  }, [row?.version, row?.id]);

  const run = async (action, fn) => {
    setBusy(action);
    try {
      return await fn();
    } finally {
      setBusy(null);
    }
  };

  // Raw JSON + the dynamic-values editors → one style_config (null if empty).
  // Tokens used in replacements but not declared become fields automatically.
  const parseStyle = () => {
    const text = styleText.trim();
    let value = {};
    if (text) {
      try {
        value = JSON.parse(text);
      } catch (e) {
        return { ok: false, error: e.message };
      }
      if (value === null || typeof value !== "object" || Array.isArray(value)) {
        return { ok: false, error: "Style config must be a JSON object" };
      }
    }
    const declared = fields
      .filter((f) => f.name.trim())
      .map((f) => ({
        name: f.name.trim().toUpperCase(),
        ...(f.description.trim() ? { description: f.description.trim() } : {}),
        ...(f.default.trim() ? { default: f.default.trim() } : {}),
      }));
    const textReplacements = Object.fromEntries(
      replacements.filter((r) => r.find.trim()).map((r) => [r.find, r.replace])
    );
    const names = new Set(declared.map((f) => f.name));
    Object.values(textReplacements).forEach((replacement) => {
      for (const match of (replacement || "").matchAll(TOKEN_PATTERN)) {
        if (!BUILTIN_TOKENS.has(match[1]) && !names.has(match[1])) {
          names.add(match[1]);
          declared.push({ name: match[1] });
        }
      }
    });
    const merged = { ...value };
    DYNAMIC_KEYS.forEach((k) => delete merged[k]);
    if (declared.length) merged.fields = declared;
    if (Object.keys(textReplacements).length) merged.text_replacements = textReplacements;
    if (dateFormat.trim()) merged.date_format = dateFormat.trim();
    return { ok: true, value: Object.keys(merged).length ? merged : null };
  };

  const updateField = (index, patch) =>
    setFields((rows) => rows.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  const updateReplacement = (index, patch) =>
    setReplacements((rows) => rows.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  const handleToggle = () =>
    run("toggle", async () => {
      try {
        await fileFormatService.updateTemplate(facilityNumber, templateId, { is_enabled: !row.is_enabled });
        toast.success(`"${row.name}" ${row.is_enabled ? "disabled" : "enabled"}.`);
        onChanged();
      } catch (error) {
        toast.error(await apiErrorMessage(error, "Failed to update template"));
      }
    });

  const handleDefault = () =>
    run("default", async () => {
      try {
        await fileFormatService.setDefault(facilityNumber, templateId, !row.is_default);
        toast.success(
          row.is_default
            ? `"${row.name}" is no longer the ${meta.ext} default — no ${meta.ext} template applies unless a user picks one.`
            : `"${row.name}" is now the default ${meta.ext} template.`
        );
        onChanged();
      } catch (error) {
        toast.error(await apiErrorMessage(error, "Failed to change the default"));
      }
    });

  const handleUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const maxBytes = schema?.template_max_bytes;
    if (maxBytes && file.size > maxBytes) {
      toast.error(`Template exceeds ${formatBytes(maxBytes)}.`);
      return;
    }
    const hasEditedOutline = (row?.content_outline || []).length > 0 && row?.has_template;
    const keepOutline = hasEditedOutline
      ? !window.confirm("Replace the current outline with the one extracted from the new template?")
      : false;
    run("upload", async () => {
      try {
        const saved = await fileFormatService.uploadTemplateFile(facilityNumber, templateId, file, { keepOutline });
        const sections = (saved?.content_outline || []).length;
        toast.success(`Template uploaded${sections ? ` — ${sections} outline items extracted` : ""}.`);
        onChanged();
      } catch (error) {
        toast.error(await apiErrorMessage(error, "Template upload failed"));
      }
    });
  };

  const handleDownloadTemplate = () =>
    run("download", async () => {
      try {
        const blob = await fileFormatService.downloadTemplateFile(facilityNumber, templateId);
        saveBlob(blob, row?.template_filename || `template.${fileFormat}`);
      } catch (error) {
        toast.error(await apiErrorMessage(error, "Template download failed"));
      }
    });

  const handleDownloadTokenized = () =>
    run("tokenized", async () => {
      try {
        const { blob, replacementsApplied } = await fileFormatService.downloadTokenizedTemplate(
          facilityNumber,
          templateId
        );
        const [base, ext] = (row?.template_filename || `template.${fileFormat}`).split(/\.(?=[^.]+$)/);
        saveBlob(blob, `${base}_tokenized.${ext || fileFormat}`);
        if (replacementsApplied === 0) {
          toast.info("No text replacements matched the template — the original file was downloaded.");
        } else {
          toast.success("Tokenized template downloaded — upload it on any environment to reuse its fields.");
        }
      } catch (error) {
        toast.error(await apiErrorMessage(error, "Tokenized download failed"));
      }
    });

  const handleExport = () =>
    run("export", async () => {
      try {
        const config = await fileFormatService.exportConfig(facilityNumber, templateId);
        const text = JSON.stringify(config, null, 2);
        const slug = (row.name || fileFormat).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
        saveBlob(new Blob([text], { type: "application/json" }), `${fileFormat}_${slug}_config_facility_${facilityNumber}.json`);
        let copied = false;
        try {
          await navigator.clipboard.writeText(text);
          copied = true;
        } catch {
          copied = false;
        }
        toast.success(`Config exported${copied ? " — downloaded and copied to clipboard" : " — downloaded"}.`);
      } catch (error) {
        toast.error(await apiErrorMessage(error, "Export failed"));
      }
    });

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setImportText(await file.text());
    setImportError(null);
  };

  const handleImport = () => {
    let config;
    try {
      config = JSON.parse(importText);
    } catch (e) {
      setImportError(`Not valid JSON: ${e.message}`);
      return;
    }
    if (!window.confirm(`Replace "${row.name}"'s style, outline, instructions and description with the imported config?`)) {
      return;
    }
    run("import", async () => {
      try {
        await fileFormatService.importConfig(facilityNumber, templateId, config);
        toast.success(`Config imported into "${row.name}". Its file, default and enable switch were left unchanged.`);
        setImportOpen(false);
        setImportText("");
        setImportError(null);
        onChanged();
      } catch (error) {
        const message = await apiErrorMessage(error, "Import failed");
        setImportError(message);
        toast.error(message);
      }
    });
  };

  const handleRemoveTemplate = () => {
    if (!window.confirm(`Remove the file from "${row.name}"? Its outline and style settings are kept.`)) return;
    run("remove", async () => {
      try {
        await fileFormatService.deleteTemplateFile(facilityNumber, templateId);
        toast.success("Template file removed.");
        onChanged();
      } catch (error) {
        toast.error(await apiErrorMessage(error, "Failed to remove template file"));
      }
    });
  };

  const handlePreview = (useDraft) => {
    let styleConfig;
    if (useDraft) {
      const parsed = parseStyle();
      if (!parsed.ok) {
        setStyleError(parsed.error);
        return;
      }
      styleConfig = parsed.value || {};
    }
    run("preview", async () => {
      try {
        const blob = await fileFormatService.preview(facilityNumber, templateId, { styleConfig });
        const slug = (row.name || "template").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
        saveBlob(blob, `preview_${slug}.${fileFormat}`);
      } catch (error) {
        toast.error(await apiErrorMessage(error, "Preview failed"));
      }
    });
  };

  const handleSave = () => {
    const parsed = parseStyle();
    if (!parsed.ok) {
      setStyleError(parsed.error);
      return;
    }
    if (!name.trim()) {
      setStyleError("Template name is required.");
      return;
    }
    setStyleError(null);
    const cleanOutline = outline
      .filter((item) => item.heading.trim())
      .map((item) => ({
        heading: item.heading.trim(),
        level: Number(item.level) || 1,
        ...(item.guidance.trim() ? { guidance: item.guidance.trim() } : {}),
      }));
    run("save", async () => {
      try {
        await fileFormatService.updateTemplate(facilityNumber, templateId, {
          name: name.trim(),
          description: description.trim() || null,
          style_config: parsed.value,
          content_outline: cleanOutline.length ? cleanOutline : null,
          content_instructions: instructions.trim() || null,
        });
        toast.success(`"${name.trim()}" saved.`);
        onChanged();
      } catch (error) {
        const message = await apiErrorMessage(error, "Failed to save settings");
        setStyleError(message);
        toast.error(message);
      }
    });
  };

  const handleDelete = () => {
    if (!window.confirm(`Delete the template "${row.name}" (file, style and outline)?`)) return;
    run("delete", async () => {
      try {
        await fileFormatService.deleteTemplate(facilityNumber, templateId);
        toast.success(`"${row.name}" deleted.`);
        setExpanded(false);
        onChanged();
      } catch (error) {
        toast.error(await apiErrorMessage(error, "Failed to delete template"));
      }
    });
  };

  const updateOutline = (index, patch) =>
    setOutline((rows) => rows.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  const defaults = schema?.styles?.[fileFormat]?.defaults;
  const configured = true;

  return (
    <div className="rounded-xl border border-gray-100">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <span className="truncate">{row.name}</span>
              {row.is_default && (
                <span className="rounded-full bg-[#2B3674]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#2B3674]">
                  Default
                </span>
              )}
              {!row.is_enabled && (
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  Disabled
                </span>
              )}
            </p>
            {row.description && <p className="truncate text-xs text-gray-600">{row.description}</p>}
            <p className="truncate text-xs text-gray-500">
              {row?.has_template
                ? `File: ${row.template_filename} · ${formatBytes(row.template_size_bytes)}`
                : meta.accept
                ? "No file uploaded"
                : "Style settings only"}
              {configured && ` · v${row.version}`}
              {row?.updated_at && ` · Updated ${formatDate(row.updated_at)}`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {meta.accept && (
            <>
              <input ref={fileInputRef} type="file" accept={meta.accept} className="hidden" onChange={handleUpload} />
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <ArrowUpTrayIcon className="h-4 w-4" />
                {busy === "upload" ? "Uploading..." : row?.has_template ? "Replace file" : "Upload file"}
              </button>
            </>
          )}
          {row?.has_template && (
            <>
              <button
                type="button"
                title="Download template"
                disabled={busy !== null}
                onClick={handleDownloadTemplate}
                className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
              </button>
              <button
                type="button"
                title="Download tokenized template — text replacements baked in as {{TOKENS}}, reusable on any environment"
                disabled={busy !== null}
                onClick={handleDownloadTokenized}
                className="rounded-lg border border-gray-200 p-2 text-[#2B3674] hover:bg-gray-50 disabled:opacity-50"
              >
                <CodeBracketSquareIcon className="h-4 w-4" />
              </button>
            </>
          )}
          <button
            type="button"
            title="Delete template"
            aria-label={`Delete template ${row.name}`}
            disabled={busy !== null}
            onClick={handleDelete}
            className="rounded-lg border border-gray-200 p-2 text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            title="Download a preview rendered with the saved settings"
            disabled={busy !== null}
            onClick={() => handlePreview(false)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <EyeIcon className="h-4 w-4" />
            {busy === "preview" ? "Rendering..." : "Preview"}
          </button>
          <button
            type="button"
            disabled={busy !== null}
            onClick={handleDefault}
            title={
              row.is_default
                ? `Remove default — no ${meta.ext} template will apply unless a user picks one`
                : `Use this template for every ${meta.ext} unless a user picks another`
            }
            className="rounded-lg px-3 py-2 text-xs font-semibold text-[#2B3674] hover:bg-gray-50 disabled:opacity-50"
          >
            {busy === "default" ? "Saving..." : row.is_default ? "Remove default" : "Set as default"}
          </button>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-xs font-semibold text-[#2B3674] hover:bg-gray-50"
          >
            Configure
            {expanded ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
          </button>
          <ToggleSwitch
            label={`Enable template ${row.name}`}
            isActive={Boolean(row?.is_enabled)}
            disabled={busy !== null}
            onToggle={handleToggle}
          />
        </div>
      </div>

      {expanded && (
        <div className="space-y-5 border-t border-gray-100 px-5 py-5">
          <p className="text-xs text-gray-500">{meta.hint}</p>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Template name</label>
              <input
                type="text"
                maxLength={120}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Sales proposal"
                className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm text-gray-800 focus:border-[#2B3674] focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Description (shown to users)</label>
              <input
                type="text"
                maxLength={1000}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. Customer-facing proposal with pricing"
                className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm text-gray-800 focus:border-[#2B3674] focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Content instructions</label>
            <p className="mb-2 text-xs text-gray-400">
              Guidance added to the AI prompt when it generates a {meta.ext} for this facility.
            </p>
            <textarea
              rows={3}
              maxLength={4000}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="e.g. Write in formal RFP language addressed to prospective vendors."
              className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm text-gray-800 focus:border-[#2B3674] focus:outline-none"
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {fileFormat === "xlsx" || fileFormat === "csv" ? "Columns" : fileFormat === "pptx" ? "Slides" : "Sections"}
                </label>
                <p className="text-xs text-gray-400">
                  The AI follows this structure in order. Extracted automatically when a template is uploaded.
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setOutline((rows) => [...rows, { key: `new-${Date.now()}`, heading: "", level: 1, guidance: "" }])
                }
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              >
                <PlusIcon className="h-4 w-4" /> Add
              </button>
            </div>
            {outline.length === 0 ? (
              <p className="rounded-lg border border-dashed border-gray-200 px-4 py-3 text-xs text-gray-400">No outline.</p>
            ) : (
              <div className="space-y-2">
                {outline.map((item, index) => (
                  <div key={item.key} className="flex items-center gap-2">
                    <select
                      value={item.level}
                      onChange={(e) => updateOutline(index, { level: Number(e.target.value) })}
                      className="rounded-lg border border-gray-200 px-2 py-2 text-xs text-gray-700"
                      title="Heading level"
                    >
                      {[0, 1, 2, 3, 4, 5, 6].map((level) => (
                        <option key={level} value={level}>
                          {level === 0 ? "Title" : `H${level}`}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      maxLength={200}
                      value={item.heading}
                      onChange={(e) => updateOutline(index, { heading: e.target.value })}
                      placeholder="Heading"
                      style={{ marginLeft: `${Math.max(0, (item.level || 1) - 1) * 16}px` }}
                      className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:border-[#2B3674] focus:outline-none"
                    />
                    <input
                      type="text"
                      maxLength={500}
                      value={item.guidance}
                      onChange={(e) => updateOutline(index, { guidance: e.target.value })}
                      placeholder="Guidance (optional)"
                      className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:border-[#2B3674] focus:outline-none"
                    />
                    <button
                      type="button"
                      title="Remove"
                      onClick={() => setOutline((rows) => rows.filter((_, i) => i !== index))}
                      className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-gray-100 p-4">
            <label className="block text-sm font-medium text-gray-700">Dynamic values</label>
            <p className="mb-3 text-xs text-gray-400">
              Make template text request-specific. Map literal template text (e.g. <code>RFP Name</code>) to a
              token like <code>{"{{RFP_NAME}}"}</code>; the AI fills each field per request. Built-in tokens:{" "}
              <code>{"{{TITLE}} {{SUBTITLE}} {{DATE}} {{FACILITY_NAME}}"}</code>. Applies only to the template's own
              header, footer and cover text.
            </p>

            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Text replacements</span>
              <button
                type="button"
                onClick={() => setReplacements((rows) => [...rows, { key: `r-new-${Date.now()}`, find: "", replace: "" }])}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              >
                <PlusIcon className="h-4 w-4" /> Add
              </button>
            </div>
            {replacements.length === 0 ? (
              <p className="mb-4 rounded-lg border border-dashed border-gray-200 px-4 py-3 text-xs text-gray-400">No replacements.</p>
            ) : (
              <div className="mb-4 space-y-2">
                {replacements.map((item, index) => (
                  <div key={item.key} className="flex items-center gap-2">
                    <input
                      type="text"
                      maxLength={300}
                      value={item.find}
                      onChange={(e) => updateReplacement(index, { find: e.target.value })}
                      placeholder="Template text, e.g. RFP Name"
                      className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:border-[#2B3674] focus:outline-none"
                    />
                    <span className="text-xs text-gray-400">→</span>
                    <input
                      type="text"
                      maxLength={500}
                      value={item.replace}
                      onChange={(e) => updateReplacement(index, { replace: e.target.value })}
                      placeholder="{{RFP_NAME}}"
                      className="flex-1 rounded-lg border border-gray-200 px-3 py-2 font-mono text-xs text-gray-800 focus:border-[#2B3674] focus:outline-none"
                    />
                    <button
                      type="button"
                      title="Remove"
                      onClick={() => setReplacements((rows) => rows.filter((_, i) => i !== index))}
                      className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Fields the AI fills per request
              </span>
              <button
                type="button"
                onClick={() =>
                  setFields((rows) => [...rows, { key: `f-new-${Date.now()}`, name: "", description: "", default: "" }])
                }
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
              >
                <PlusIcon className="h-4 w-4" /> Add
              </button>
            </div>
            {fields.length === 0 ? (
              <p className="mb-4 rounded-lg border border-dashed border-gray-200 px-4 py-3 text-xs text-gray-400">
                No fields. Tokens used in replacements or found in an uploaded template are added automatically.
              </p>
            ) : (
              <div className="mb-4 space-y-2">
                {fields.map((item, index) => (
                  <div key={item.key} className="flex items-center gap-2">
                    <input
                      type="text"
                      maxLength={40}
                      value={item.name}
                      onChange={(e) =>
                        updateField(index, { name: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_") })
                      }
                      placeholder="RFP_NAME"
                      className="w-44 rounded-lg border border-gray-200 px-3 py-2 font-mono text-xs text-gray-800 focus:border-[#2B3674] focus:outline-none"
                    />
                    <input
                      type="text"
                      maxLength={300}
                      value={item.description}
                      onChange={(e) => updateField(index, { description: e.target.value })}
                      placeholder="What the AI should put here, e.g. short name of this RFP"
                      className="flex-[2] rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:border-[#2B3674] focus:outline-none"
                    />
                    <input
                      type="text"
                      maxLength={500}
                      value={item.default}
                      onChange={(e) => updateField(index, { default: e.target.value })}
                      placeholder="Default (optional)"
                      className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:border-[#2B3674] focus:outline-none"
                    />
                    <button
                      type="button"
                      title="Remove"
                      onClick={() => setFields((rows) => rows.filter((_, i) => i !== index))}
                      className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-400">
              {"{{DATE}}"} format
            </label>
            <input
              type="text"
              maxLength={40}
              value={dateFormat}
              onChange={(e) => setDateFormat(e.target.value)}
              placeholder="%B %d, %Y  (e.g. %b %-d %Y → Sep 9 2026)"
              className="w-72 rounded-lg border border-gray-200 px-3 py-2 font-mono text-xs text-gray-800 focus:border-[#2B3674] focus:outline-none"
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-700">Style overrides (JSON)</label>
                <p className="text-xs text-gray-400">
                  Only the keys you set are applied; with a template, the template's own look is kept for everything else.
                </p>
              </div>
              {defaults && (
                <button
                  type="button"
                  onClick={() => setShowDefaults((v) => !v)}
                  className="text-xs font-semibold text-[#2B3674] hover:underline"
                >
                  {showDefaults ? "Hide" : "Show"} available options
                </button>
              )}
            </div>
            <textarea
              rows={8}
              spellCheck={false}
              value={styleText}
              onChange={(e) => {
                setStyleText(e.target.value);
                setStyleError(null);
              }}
              placeholder={'{\n  "footer": { "text": "{{FACILITY_NAME}} – Confidential", "show_page_number": true }\n}'}
              className={`w-full rounded-lg border px-3.5 py-2.5 font-mono text-xs text-gray-800 focus:outline-none ${
                styleError ? "border-red-300 focus:border-red-400" : "border-gray-200 focus:border-[#2B3674]"
              }`}
            />
            {styleError && <p className="mt-1 text-xs text-red-600">{styleError}</p>}
            {showDefaults && defaults && (
              <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
                {JSON.stringify(defaults, null, 2)}
              </pre>
            )}
          </div>

          <div className="rounded-xl border border-gray-100 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Move to another environment</label>
                <p className="text-xs text-gray-400">
                  Export this format's settings (fields, replacements, date format, outline, instructions) and import
                  them elsewhere. Upload the tokenized template there separately; the enable switch is never imported.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={busy !== null || !configured}
                  onClick={handleExport}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  {busy === "export" ? "Exporting..." : "Export config"}
                </button>
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() => {
                    setImportOpen((v) => !v);
                    setImportError(null);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  Import config
                </button>
              </div>
            </div>
            {importOpen && (
              <div className="mt-3 space-y-2">
                <textarea
                  rows={6}
                  spellCheck={false}
                  value={importText}
                  onChange={(e) => {
                    setImportText(e.target.value);
                    setImportError(null);
                  }}
                  placeholder="Paste an exported config JSON here, or choose a .json file"
                  className={`w-full rounded-lg border px-3.5 py-2.5 font-mono text-xs text-gray-800 focus:outline-none ${
                    importError ? "border-red-300" : "border-gray-200 focus:border-[#2B3674]"
                  }`}
                />
                {importError && <p className="text-xs text-red-600">{importError}</p>}
                <div className="flex items-center justify-between">
                  <div>
                    <input ref={importFileRef} type="file" accept=".json,application/json" className="hidden" onChange={handleImportFile} />
                    <button
                      type="button"
                      onClick={() => importFileRef.current?.click()}
                      className="text-xs font-semibold text-[#2B3674] hover:underline"
                    >
                      Choose .json file
                    </button>
                  </div>
                  <button
                    type="button"
                    disabled={busy !== null || !importText.trim()}
                    onClick={handleImport}
                    className="rounded-xl bg-[#2B3674] px-4 py-2 text-xs font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
                  >
                    {busy === "import" ? "Importing..." : "Apply import"}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <button
                type="button"
                disabled={busy !== null || !configured}
                onClick={handleDelete}
                className="text-xs font-semibold text-red-600 hover:underline disabled:opacity-40"
              >
                Delete template
              </button>
              {row?.has_template && (
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={handleRemoveTemplate}
                  title="Remove only the uploaded file; style and outline are kept"
                  className="text-xs font-semibold text-gray-500 hover:underline disabled:opacity-40"
                >
                  Remove file only
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => handlePreview(true)}
                className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {busy === "preview" ? "Rendering..." : "Preview draft style"}
              </button>
              <button
                type="button"
                disabled={busy !== null}
                onClick={handleSave}
                className="rounded-xl bg-[#2B3674] px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
              >
                {busy === "save" ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileFormatCard;
